import apiClient from "../api/axios";
import alertService from "../components/alert/alertService";
import { handleApiError } from "../api/handleApiError";
import {
  Members,
  ResponseMembers,
} from "../models/dto/response/ResponseMembers";
import { User } from "../models/User";

const UsersService = {
  getUsers: async (): Promise<Members | null> => {
    try {
      alertService.showLoading();
      const response = await apiClient.get("/v1/users");
      const result: ResponseMembers = response.data.results;
      return result.members;
    } catch (error: any) {
      handleError(error);
    } finally {
      alertService.closeLoading();
    }
    return null;
  },
  invite: async (formData: any): Promise<Members | null> => {
    try {
      alertService.showLoading();
      const response = await apiClient.post("/v1/users/invite", formData);
      const result: ResponseMembers = response.data.results;
      return result.members;
    } catch (error: any) {
      handleError(error);
    } finally {
      alertService.closeLoading();
    }
    return null;
  },
  activate: async (
    userRefId: string,
    formData: any,
  ): Promise<boolean | null> => {
    try {
      alertService.showLoading();
      await apiClient.post(`/v1/users/${userRefId}/activate`, formData);
      return true;
    } catch (error: any) {
      handleError(error);
    } finally {
      alertService.closeLoading();
    }
    return false;
  },
  resetPassword: async (
    currentPassword: string,
    newPassword: string,
  ): Promise<boolean | null> => {
    try {
      alertService.showLoading();
      await apiClient.patch(`/v1/users/password`, {
        currentPassword: currentPassword,
        newPassword: newPassword,
      });
      return true;
    } catch (error: any) {
      handleError(error);
    } finally {
      alertService.closeLoading();
    }
    return false;
  },
  deleteUser: async (refId: string): Promise<Members | null> => {
    try {
      alertService.showLoading();
      const response = await apiClient.patch(`/v1/users/${refId}/soft-delete`);
      return response.data.results.members;
    } catch (error: any) {
      handleError(error);
    } finally {
      alertService.closeLoading();
    }
    return null;
  },

  forgetPassword: async (email: string): Promise<boolean> => {
    try {
      alertService.showLoading();
      await apiClient.post(`/v1/users/forget-password`, { email });
      return true;
    } catch (error: unknown) {
      handleError(error);
    } finally {
      alertService.closeLoading();
    }
    return false;
  },
  recoveryPassword: async (
    resetToken: string,
    password: string,
  ): Promise<boolean> => {
    try {
      alertService.showLoading();
      await apiClient.post(`/v1/users/reset-password`, {
        resetToken: resetToken,
        password: password,
      });
      return true;
    } catch (error: unknown) {
      handleError(error);
    } finally {
      alertService.closeLoading();
    }
    return false;
  },
  loginUser: async (email: string, password: string): Promise<User> => {
    const response = await apiClient.post("/v1/users/login", {
      email,
      password,
    });
    return response.data.results.user;
  },

  fetchUserInfo: async (): Promise<User> => {
    const response = await apiClient.get("/v1/users/info");
    return response.data.results.user;
  },

  // ไม่ผ่าน handleApiError — ผู้เรียกต้องรู้ผลสำเร็จ/ล้มเหลวเองเพื่อตัดสินใจเรื่อง uncertainty notice
  logoutUser: async (): Promise<void> => {
    await apiClient.post("/v1/users/logout", undefined, {
      _suppressGlobalAlert: true,
    } as any);
  },

  unlockAccount: async (unlockToken: string): Promise<boolean> => {
    try {
      alertService.showLoading();
      await apiClient.post(`/v1/users/unlock`, { unlockToken });
      return true;
    } catch (error: unknown) {
      handleError(error);
    } finally {
      alertService.closeLoading();
    }
    return false;
  },
};

const handleError = (error: unknown) =>
  handleApiError(
    error,
    {
      FORBIDDEN_ACCESS_ERROR: "Cannot-Change-Organization-Due-To-Permission-Error",
      USERS_RETRIEVAL_PROCESS_FAILED: "failed_to_retrieve_users_in_organization",
      USER_ALREADY_HAS_ACCESS: "User-Already-Has-Invitation-To-Organization-Error",
      USER_ALREADY_ACTIVATED: {
        messageKey: "User-Already-Has-Invitation-To-Organization-Error",
        afterAlert: () => { window.location.href = "/login"; },
      },
      WRONG_PASSWORD: "incorrect-password",
      USER_NOT_FOUND: "Please-Check-Your-Email-Or-Activate-Link",
      USER_ALREADY_HAS_DIFFERENT_ROLE: "User-Already-Has-Different-Role",
      RESET_TOKEN_EXPIRED: {
        messageKey: "Password-Reset-Link-Invalid",
        afterAlert: () => { window.location.href = "/forgot-password"; },
      },
      UNLOCK_TOKEN_INVALID: "Unlock-Token-Invalid",
      UNLOCK_TOKEN_EXPIRED: "Unlock-Token-Expired",
      ACCOUNT_ALREADY_UNLOCKED: {
        messageKey: "Account-Already-Unlocked",
        afterAlert: () => { window.location.href = "/login"; },
      },
    },
    "Cannot-Access-Organization-Error",
  );

export default UsersService;
